import { ApiError } from '@clockwise/shared';
import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Surface a TanStack Query error as a single sonner toast. Resource-keyed
 * so repeat errors collapse into one visible notification, and so each
 * resource (customer, product, in-scope products, …) has a stable id the
 * test suite can target.
 *
 * Pass `null`/`undefined` to no-op — convenient inside a component that
 * conditionally surfaces errors only for the active query.
 *
 * UNAUTHENTICATED errors are suppressed: the global QueryCache handler in
 * main.tsx redirects to /login on session expiry, so a "Could not load X"
 * toast would just flash misleadingly right before the redirect.
 */
export function useFetchErrorToast(error: unknown, resource: string): void {
  useEffect(() => {
    if (!error) return;
    if (error instanceof ApiError && error.code === 'UNAUTHENTICATED') return;
    toast.error(`Could not load ${resource}. Refresh to retry.`, {
      id: `${slugify(resource)}-fetch-error`,
    });
  }, [error, resource]);
}

function slugify(resource: string): string {
  return resource.trim().toLowerCase().replace(/\s+/g, '-');
}
